// Adapted from apps/admin/src/features/map-view/model/helpers/step-pin-items.ts (PRD #875):
// InfoType comes with MapObject, govdash having no info panel.
import { ConnectProto } from "@racoon/api";
import { fromTimestamp, getEnumName, isValidRoutePoint, sortJobsByStep } from "@racoon/lib";

import type { InfoType, MapObject } from "../../ui/map-view";
import { pickupLocationPolygons, polygonsCenter } from "../geofence-polygons";
import { jobStatusTone } from "../constants/job-status-colors";

/** A job with the number its pin prints. */
export type NumberedJob = { job: ConnectProto.Job; step: string };

type Route = Pick<ConnectProto.Route, "id" | "jobs" | "planStartAt" | "factStartAt">;

const startOf = (route: Route) =>
  (fromTimestamp(route.factStartAt) ?? fromTimestamp(route.planStartAt))?.getTime() ??
  Number.MAX_SAFE_INTEGER;

// Connect ids may arrive as decimal strings; Number() would round large ones together.
const byId = (a: Route, b: Route) => {
  const [x, y] = [BigInt(String(a.id)), BigInt(String(b.id))];
  return x < y ? -1 : x > y ? 1 : 0;
};

/**
 * Numbers routes' jobs for their pins. "step" is a job's place in its own trip, as the route
 * tables number it; "day" counts through a calendar day's trips in order, so a truck's day
 * with two shifts never repeats a number, and restarts at 1 on the next day.
 */
export function numberRouteJobs(routes: Route[], mode: "step" | "day"): NumberedJob[] {
  const out: NumberedJob[] = [];
  const trips = [...routes].sort((a, b) => startOf(a) - startOf(b) || byId(a, b));
  let day = "";
  let count = 0;
  for (const route of trips) {
    const tripDay = new Date(startOf(route)).toDateString();
    if (tripDay !== day) [day, count] = [tripDay, 0];
    sortJobsByStep(route.jobs).forEach((job, index) => {
      const step = mode === "day" ? ++count : Number(job.stepIdx ?? 0) || index + 1;
      out.push({ job, step: String(step) });
    });
  }
  return out;
}

/**
 * One pin per job for StepPickupLayer: jobs sharing a site fan around it when zoomed in
 * and fold into one pin further out. A site with a geofence also gets a shape-only item.
 */
export function buildStepPins(jobs: NumberedJob[]): MapObject[] {
  const bySite = new Map<bigint, NumberedJob[]>();
  for (const entry of jobs) {
    if (!isValidRoutePoint(entry.job.pickupLocation)) continue;
    const key = entry.job.pickupLocationId;
    bySite.set(key, [...(bySite.get(key) ?? []), entry]);
  }

  const result: MapObject[] = [];
  let siteIndex = 1;
  for (const group of bySite.values()) {
    const site = group[0].job;
    const polygons = pickupLocationPolygons(site.pickupLocation?.geometry);
    // A fan over a geofence hangs from the shape's centre, not the site's pin point.
    const [lat, lon] =
      polygons.length && group.length > 1
        ? polygonsCenter(polygons)
        : [site.pickupLocation?.lat ?? 0, site.pickupLocation?.lon ?? 0];
    // Left empty when unnamed: the tooltip renders the localized fallback.
    const name = site.pickupLocation?.name ?? "";
    if (polygons.length) {
      const tones = new Set(group.map(({ job }) => jobStatusTone(job.status)?.bg));
      result.push({
        id: `${site.pickupLocationId}-shape`,
        name,
        type: "location" as InfoType,
        lat,
        lon,
        jobStatus: tones.size === 1 ? site.status : undefined,
        companyName: site.serviceReceiver?.name,
        pinLabel: "",
        geometry: site.pickupLocation?.geometry,
        shapeOnly: true,
      });
    }
    group.forEach(({ job, step }, index) => {
      result.push({
        id: `${job.pickupLocationId}-${job.id}`,
        name,
        type: "location" as InfoType,
        lat,
        lon,
        status: getEnumName(ConnectProto.JobStatusSchema, job.status),
        jobStatus: job.status,
        companyName: job.serviceReceiver?.name,
        localIndex: siteIndex,
        pinLabel: step,
        jobId: job.id,
        siteKey: String(job.pickupLocationId),
        fan: { index, count: group.length },
        // No geometry: the shape-only item above draws it, and a step item with
        // one would be drawn as a polygon instead of its numbered pin when zoomed in.
      });
    });
    siteIndex++;
  }
  return result;
}
